import {
  Slider,
  SliderProps,
  Stack,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { ChangeEvent, forwardRef, KeyboardEvent, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type TProps = {
  name: string;
  onChange?: () => void;
  showSlider?: boolean;
  sliderProps?: SliderProps;
  noDecimal?: boolean;
  nullable?: boolean;
  InputProps?: TextFieldProps['InputProps'];
};

type Props = TProps & TextFieldProps & { max?: number };

const RHFNumber = forwardRef(
  (
    {
      name,
      onChange: propsOnChange,
      showSlider,
      sliderProps,
      noDecimal = false,
      nullable = false,
      max,
      InputProps,
      ...other
    }: Props,
    ref
  ) => {
    const { setValue, getValues } = useFormContext();

    const preventKeys = (event: KeyboardEvent<HTMLInputElement>) => {
      if (
        event.code === 'Minus' ||
        event.key === 'e' ||
        (noDecimal && event.key === '.')
      ) {
        event.preventDefault();
      }
    };

    useEffect(() => {
      const value = !nullable && getValues(name) === '' ? '0' : getValues(name);
      setValue(name, value);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Controller
        name={name}
        render={({ field: { onChange, ...field }, fieldState: { error } }) => {
          const _onChange = (event: ChangeEvent<HTMLInputElement>) => {
            let _value: string | null = event.target.value;

            // remove leading zeros
            if (
              event.target.value.charAt(0) === '0' &&
              event.target.value.charAt(1) === '0'
            ) {
              event.target.value = '0';
            }

            //  if input is a valid number, set _value to null (if nullable) or '0'
            if (isNaN(Number(event.target.value))) {
              _value = nullable ? null : '0';
            }

            if (max !== undefined && Number(event.target.value) > max) {
              _value = max?.toString();
            }

            // if noDecimal flag is true, truncate decimal and convert to string
            if (noDecimal) {
              _value = Math.trunc(Number(event.target.value)).toString();
            }

            // input length is 2, starts with '0' and doesn't contain '.', remove leading '0'
            if (
              event.target.value.length === 2 &&
              event.target.value.charAt(0) === '0' &&
              event.target.value.charAt(1) !== '.'
            ) {
              _value = '' + Number(event.target.value || '0');
              event.target.value = event.target.value.substring(
                1,
                event.target.value.length
              );
            }

            // if input is empty and not nullable, set _value to '0'
            if (!nullable && event.target.value === '') {
              _value = '0';
            }

            // if input is empty and nullable, set _value to null
            if (nullable && event.target.value === '') {
              _value = null;
            }

            onChange(
              _value === null
                ? null
                : isNaN(Number(_value))
                  ? _value
                  : Number(_value)
            );
          };

          return (
            <Stack>
              {/* @ts-ignore */}
              <TextField
                {...field}
                variant='outlined'
                type={isNaN(field.value) ? 'text' : 'number'}
                onKeyDown={preventKeys}
                autoComplete='off'
                inputRef={ref}
                onChange={_onChange}
                fullWidth
                error={!!error}
                helperText={error?.message}
                inputProps={{
                  inputMode: 'decimal',
                  pattern: '[0-9]*',
                  max,
                }}
                InputProps={{
                  ...(InputProps || {}),
                }}
                InputLabelProps={{
                  shrink:
                    (field.value !== null &&
                      field.value !== undefined &&
                      field.value !== '' &&
                      field.value !== '.') ||
                    nullable,
                }}
                {...other}
              />
              {showSlider && (
                <Slider
                  {...sliderProps}
                  value={field.value || 0}
                  onChange={(_, value) => {
                    onChange(value);
                    if (propsOnChange) {
                      propsOnChange();
                    }
                  }}
                />
              )}
            </Stack>
          );
        }}
      />
    );
  }
);

export default RHFNumber;
